import pdfMake from 'addthaifont-pdfmake';
import 'addthaifont-pdfmake/build/vfs_fonts';
import buddhistEra from 'dayjs/plugin/buddhistEra';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import dayjs from 'dayjs';
import _ from 'lodash';

import { getBankLabel } from '../../constants/thaiBanks';
pdfMake.fonts = {
  Sarabun: {
    normal: 'Sarabun-Light.ttf',
    bold: 'Sarabun-Regular.ttf',
    italics: 'Sarabun-LightItalic.ttf',
    bolditalics: 'Sarabun-Italic.ttf',
  },
};

dayjs.locale('th');
dayjs.extend(buddhistEra);
dayjs.extend(LocalizedFormat);

const formatMoney = (val) =>
  (val || 0)?.toFixed(2)?.replace(/\d(?=(\d{3})+\.)/g, '$&,');

const paymentStatusLabel = {
  PENDING: 'pending',
  PARTIAL: 'partial',
  SUCCESS: 'success',
};

const genBody = (rows = []) => {
  const tableRows = [];
  _.forEach(rows, (row, index) => {
    const expenses = row?.expenses_list || [];
    const numExpenses = Math.max(1, expenses.length);

    for (let i = 0; i < numExpenses; i++) {
      const it = expenses[i];
      const isFirst = i === 0;

      let left = '-';
      let detail = '';
      let net = 0;
      let code = '';
      if (it) {
        const budgetCode = `${it?.budget?.prefix || ''}${
          it?.budget?.budget_number || ''
        }`.trim();
        const proj = (it?.project?.project_number || '').trim();
        const joined = [proj, budgetCode].filter(Boolean).join(' | ');
        left = joined || '-';
        code = (it?.code || '').trim();

        net = (it?.price || 0) - (it?.tax || 0);
        detail = `${i + 1}. ${it?.name || ''}`;
      }

      const tr = [
        ...(isFirst
          ? [
              {
                rowSpan: numExpenses,
                text: index + 1,
                alignment: 'center',
                fontSize: 8,
                border: [false, false, false, false],
              },
              {
                rowSpan: numExpenses,
                text: [
                  { text: `${row?.payment_number || ''}` },
                  { text: '\n' },
                  {
                    text: paymentStatusLabel[row?.status] || row?.status || '',
                    fontSize: 7,
                  },
                ],
                alignment: 'center',
                fontSize: 8,
                border: [false, false, false, false],
              },
              {
                rowSpan: numExpenses,
                text: `${row?.payee?.name || ''}`,
                alignment: 'center',
                fontSize: 8,
                border: [false, false, false, false],
              },
              {
                rowSpan: numExpenses,
                text: [
                  { text: row?.payee?.account_number || '' },
                  { text: '\n' },
                  { text: getBankLabel(row?.payee?.bank) || '' },
                ],
                alignment: 'center',
                fontSize: 8,
                border: [false, false, false, false],
              },
              {
                rowSpan: numExpenses,
                text: `${row?.expenses_list?.length || 0}`,
                alignment: 'center',
                fontSize: 8,
                border: [false, false, false, false],
              },
              {
                rowSpan: numExpenses,
                text: `${formatMoney(
                  _.sumBy(
                    row?.expenses_list || [],
                    (x) => (x?.price || 0) - (x?.tax || 0),
                  ) ||
                    row?.totalAmount ||
                    0,
                )}`,
                alignment: 'center',
                fontSize: 8,
                border: [false, false, false, false],
              },
            ]
          : [{}, {}, {}, {}, {}, {}]),
        {
          text: it
            ? [
                { text: `${i + 1}. ${left}` },
                ...(code ? [{ text: '\n' }, { text: code }] : []),
              ]
            : `${i + 1}. ${left}`,
          alignment: 'center',
          fontSize: 7,
          border: [false, false, false, false],
        },
        {
          text: it ? formatMoney(net) : '-',
          alignment: 'center',
          fontSize: 7,
          border: [false, false, false, false],
        },
        { text: detail, fontSize: 7, border: [false, false, false, false] },
      ];
      tableRows.push(tr);
    }
  });
  return tableRows;
};

// Signature ให้เหมือน expensesReport: (result, info)
const paymentDailyReport = async (result, info) => {
  const rows = [...(result?.rows || [])].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
  );
  const body = genBody(rows);
  const total = _.sumBy(
    rows,
    (r) =>
      _.sumBy(
        r?.expenses_list || [],
        (it) => (it?.price || 0) - (it?.tax || 0),
      ) ||
      Number(r?.totalAmount) ||
      0,
  );
  const totalFormatted = formatMoney(total);

  const documentRef = {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [15, 90, 15, 60],
    defaultStyle: {
      font: 'Sarabun',
      fontSize: 10,
    },
    info: {
      title: 'Payment Daily Report',
      author: info?.name || '',
      subject: 'Payment Daily Report',
    },
    header: [
      {
        margin: [20, 15, 20, 0],
        stack: [
          {
            text: 'รายงานการจ่ายเงินประจำวัน',
            style: 'headerTitle',
            alignment: 'center',
          },
          {
            text: `${info?.name || ''}`,
            style: 'headerCompany',
            alignment: 'center',
            margin: [0, 4, 0, 0],
          },
          {
            columns: [
              {
                text: `วันที่: ${
                  info?.date ? dayjs(info.date).format('DD/MM/YYYY') : '-'
                }`,
                style: 'headerDate',
                width: 'auto',
              },
              {
                text: `ยอดรวม: ${totalFormatted} บาท`,
                style: 'headerTotal',
                width: '*',
                alignment: 'right',
              },
            ],
            margin: [0, 10, 0, 0],
          },
        ],
      },
    ],
    content: [
      {
        table: {
          headerRows: 1,
          widths: [20, 55, 95, 100, 30, 60, 100, 65, 210],
          body: [
            [
              {
                text: 'ลำดับ',
                fillColor: '#d2d4d2',
                alignment: 'center',
                fontSize: 8,
                border: [false, false, false, true],
              },
              {
                text: 'เลขที่เอกสาร',
                fillColor: '#d2d4d2',
                alignment: 'center',
                fontSize: 8,
                border: [false, false, false, true],
              },
              {
                text: 'ผู้รับเงิน',
                fillColor: '#d2d4d2',
                alignment: 'center',
                fontSize: 8,
                border: [false, false, false, true],
              },
              {
                text: 'เลขบัญชี/ธนาคาร',
                fillColor: '#d2d4d2',
                alignment: 'center',
                fontSize: 8,
                border: [false, false, false, true],
              },
              {
                text: 'จำนวนรายการ',
                fillColor: '#d2d4d2',
                alignment: 'center',
                fontSize: 8,
                border: [false, false, false, true],
              },
              {
                text: 'ยอดรวม',
                fillColor: '#d2d4d2',
                alignment: 'center',
                fontSize: 8,
                border: [false, false, false, true],
              },
              {
                text: 'งาน-รหัสงบ',
                fillColor: '#d2d4d2',
                alignment: 'center',
                fontSize: 8,
                border: [false, false, false, true],
              },
              {
                text: 'ยอดเบิก',
                fillColor: '#d2d4d2',
                alignment: 'center',
                fontSize: 8,
                border: [false, false, false, true],
              },
              {
                text: 'รายละเอียดรายการย่อย',
                fillColor: '#d2d4d2',
                alignment: 'center',
                fontSize: 8,
                border: [false, false, false, true],
              },
            ],
            ...body,
            [
              {
                text: 'รวมเงินทั้งหมด',
                fillColor: '#d2d4d2',
                alignment: 'center',
                colSpan: 5,
                border: [false, false, false, true],
              },
              {},
              {},
              {},
              {},
              {
                text: totalFormatted,
                alignment: 'center',
                border: [false, false, false, true],
                bold: true,
              },
              {},
              {},
              {},
            ],
          ],
        },
      },
    ],
    footer(currentPage, pageCount) {
      return {
        margin: [20, 0],
        table: {
          widths: ['*', 'auto'],
          body: [
            [
              {
                text: `พิมพ์เมื่อ: ${dayjs().format('DD/MM/YYYY HH:mm')} น.`,
                fontSize: 8,
                alignment: 'left',
                border: [false, false, false, false],
              },
              {
                text: `หน้า ${currentPage} / ${pageCount}`,
                alignment: 'right',
                fontSize: 8,
                border: [false, false, false, false],
              },
            ],
          ],
        },
        layout: 'noBorders',
      };
    },
    styles: {
      headerTitle: {
        fontSize: 16,
        bold: true,
        color: '#1a1a1a',
      },
      headerCompany: {
        fontSize: 12,
        bold: true,
        color: '#333333',
      },
      headerDate: {
        fontSize: 16,
        color: '#1A1A1A',
      },
      headerTotal: {
        fontSize: 18,
        bold: true,
        color: '#d32f2f',
      },
    },
  };

  pdfMake.createPdf(documentRef).open();
};

export default paymentDailyReport;
